import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  usePendingMutation,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type StreamSignal = {
  id: string;
  projectID: string;
  number: number;
  name: string;
  description: string;
  sortOrder: number;
  videoFeedID: string | null;
  videoFeed?: { id: string; name: string } | null;
  audioFeedLinks?: Array<{
    id: string;
    signalID: string;
    audioFeedID: string;
    sortOrder: number;
    audioFeed?: { id: string; name: string } | null;
  }>;
};

export const useStreamSignals = (projectId: string | undefined) => {
  const query = projectId
    ? appQueries.streamSignals.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const signals = (data ?? []) as StreamSignal[];
  return {
    data: signals,
    isLoading:
      !!projectId && details.type !== "complete" && signals.length === 0,
  };
};

export const useCreateStreamSignal = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: {
        projectID: string;
        number: number;
        name?: string;
        description?: string;
        videoFeedID?: string | null;
        sortOrder?: number;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamSignals.create({
            id: generateUUID(),
            projectID: input.projectID,
            number: input.number,
            name: input.name ?? "",
            description: input.description ?? "",
            videoFeedID: input.videoFeedID ?? null,
            sortOrder: input.sortOrder ?? 0,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Signal create was rejected",
        );
      })
        .then(() => {
          toast.success("Signal added");
          options?.onSuccess?.();
        })
        .catch((err) => toast.error("Failed to add signal: " + err.message));
    },
    isPending,
  };
};

export const useUpdateStreamSignal = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: {
        id: string;
        projectID: string;
        name?: string;
        description?: string;
        videoFeedID?: string | null;
        number?: number;
        sortOrder?: number;
      },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(appMutators.streamSignals.update(input));
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Signal update was rejected",
        );
      })
        .then(() => {
          options?.onSuccess?.();
        })
        .catch((err) => toast.error("Failed to update signal: " + err.message));
    },
    isPending,
  };
};

export const useDeleteStreamSignal = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: { onSuccess?: () => void },
    ) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamSignals.delete({
            id: input.id,
            projectID: input.projectId,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Signal delete was rejected",
        );
      })
        .then(() => {
          toast.success("Signal removed");
          options?.onSuccess?.();
        })
        .catch((err) => toast.error("Failed to remove signal: " + err.message));
    },
    isPending,
  };
};

export const useAddSignalAudioFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (input: {
      signalID: string;
      audioFeedID: string;
      sortOrder?: number;
    }) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamSignalAudioFeeds.create({
            id: generateUUID(),
            signalID: input.signalID,
            audioFeedID: input.audioFeedID,
            sortOrder: input.sortOrder ?? 0,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Signal audio feed link was rejected",
        );
      }).catch((err) =>
        toast.error("Failed to link audio feed: " + err.message),
      );
    },
    isPending,
  };
};

export const useUpdateSignalAudioFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (input: { id: string; signalID: string; sortOrder: number }) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamSignalAudioFeeds.update({
            id: input.id,
            signalID: input.signalID,
            sortOrder: input.sortOrder,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Signal audio feed update was rejected",
        );
      }).catch((err) =>
        toast.error("Failed to update audio feed link: " + err.message),
      );
    },
    isPending,
  };
};

export const useRemoveSignalAudioFeed = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  return {
    mutate: (input: { id: string; signalID: string }) => {
      void runPending(async () => {
        const mutation = zero.mutate(
          appMutators.streamSignalAudioFeeds.delete({
            id: input.id,
            signalID: input.signalID,
          }),
        );
        await mutation.client;
        await reportServerMutationFailure(
          mutation.server,
          "Signal audio feed unlink was rejected",
        );
      }).catch((err) =>
        toast.error("Failed to unlink audio feed: " + err.message),
      );
    },
    isPending,
  };
};
